import { trackEvent } from "~/utils/monitoring";

// Analytics for the nextgen Chat experience, branded "Nova" in the product and
// in Mixpanel. Event names are deliberately shared with the legacy Dataset
// Assistant (`components/assistant` and the dataset assistant-v2 wrapper) so a
// single Mixpanel dashboard can pivot both surfaces on the `context` property:
// these events set `context: "nova"` where the dataset assistant sets
// `context: "dataset"`.
export const NEXTGEN_CHAT_CONTEXT = "nova";

// The identifiers a nextgen chat event carries when they are in scope. All
// optional so a navigation-level site (e.g. a starter-prompt click that
// predates any conversation) can emit with only what it has.
export interface NextgenChatEventContext {
  conversationId?: string | null;
  projectId?: string | null;
  agentId?: string | null;
  agentName?: string | null;
}

// Wire names for nextgen chat + project events. Every event is separated from the
// legacy Dataset Assistant purely by the `context` property (`"nova"` vs
// `"dataset"`): a chat-funnel action reuses its Dataset Assistant twin's event
// name, and a Nova-only concept (projects, sharing, connectors) uses an
// unprefixed name too — the surface is carried by `context`, never a name prefix.
export const NEXTGEN_CHAT_EVENT = {
  CONVERSATION_OPENED: "assistant_conversation_selected",
  NEW_CHAT: "assistant_new_chat_clicked",
  CONVERSATION_DELETED: "assistant_conversation_deleted",
  CONVERSATION_RENAMED: "assistant_conversation_renamed",
  CONVERSATION_SHARED: "conversation_shared",
  MESSAGE_SENT: "analysis_agent_message_sent",
  MESSAGE_RECEIVED: "analysis_agent_message_received",
  MESSAGE_ERROR: "analysis_agent_message_error",
  MESSAGE_CANCELLED: "analysis_agent_message_cancelled",
  PRESET_CLICKED: "analysis_agent_preset_clicked",
  PROJECT_OPENED: "project_opened",
  PROJECT_CREATED: "project_created",
  MESSAGE_FEEDBACK_SUBMITTED: "analysis_agent_message_feedback_v2",
  MESSAGE_FEEDBACK_CLEARED: "analysis_agent_message_feedback_v2_cleared",
  ARTIFACT_DOWNLOADED: "analysis_agent_artifact_downloaded",
  ARTIFACT_DOWNLOAD_FAILED: "analysis_agent_artifact_download_failed",
  ARTIFACT_EXPORT_COMPLETED: "analysis_agent_artifact_export_completed",
  ARTIFACT_EXPORT_FAILED: "analysis_agent_artifact_export_failed",
} as const;
export type NextgenChatEvent = (typeof NEXTGEN_CHAT_EVENT)[keyof typeof NEXTGEN_CHAT_EVENT];

// Emit a nextgen chat analytics event, stamping `context: "nova"` and any
// in-scope ids so every call site reports a consistent shape. Absent ids are
// dropped rather than sent as null so the Mixpanel event stays clean. Extra
// per-event properties are merged on top.
export const trackNextgenChatEvent = (
  event: NextgenChatEvent,
  context: NextgenChatEventContext,
  properties: Record<string, unknown> = {}
): void => {
  trackEvent(event, {
    context: NEXTGEN_CHAT_CONTEXT,
    ...(context.conversationId ? { conversation_id: context.conversationId } : {}),
    ...(context.projectId ? { project_id: context.projectId } : {}),
    ...(context.agentId ? { agent_id: context.agentId } : {}),
    ...(context.agentName ? { agent_name: context.agentName } : {}),
    ...properties,
  });
};
